import orderBy from 'lodash/orderBy';
import find from 'lodash/find';
import { captureWarnMessage } from '../tracker/raven';
import { safeStringify } from './objectTransforms';

export const detectionTypes = {
    whitelistHead: 'whitelist_head',
    whitelistComplement: 'whitelist_complement',
    blacklist: 'blacklist',
    normal: 'normal'
};

interface Spotting {
    startIndex: number;
    endIndex: number;
    type: string;
}
export interface TranscriptRenderWord {
    text: string;
    source: string;
    spotting: string;
    anonymized: boolean;
    confidence?: number;
    pronunciation?: { sound: string; confidence: number }[] | null;
    wordOffsetStart?: number;
    wordOffsetEnd?: number;
}

export interface TranscriptRenderChunk {
    render: TranscriptRenderWord[];
    text: string;
}

export interface CombinedTranscriptItem extends TranscriptItem {
    transcript: TranscriptRenderChunk[];
}
export interface TranscriptWordItem {
    normalizedWord: string;
    punctuation: { name: string };
    words: { anonymized?: boolean; startTimeMsec?: number; endTimeMsec?: number }[];
    confidence?: number;
    pronunciation?: { sound: string; confidence: number }[] | null;
}
export interface PhraseItem {
    text: string;
    alternatives: { text: string }[];
    complements?: { text: string }[];
}
export interface BlacklistPhraseItem {
    text: string;
}
export interface TranscriptItem {
    normalizedTranscript: string;
    words: TranscriptWordItem[];
    type: string;
    startOffset: number;
    endOffset: number;
}

const getSpottingIndicesForWhitelist = (transcript: string, list: PhraseItem[], currentList: Spotting[] = []) => {
    const result: Spotting[] = [];
    const lowerTranscript = transcript.toLowerCase();
    list.forEach(item => {
        let headSpot: Spotting | undefined;
        item.alternatives.find(({ text }) => {
            const headIndex = lowerTranscript.indexOf(text.toLowerCase());
            const headEndIndex = headIndex + text.length - 1;

            const interception = find(currentList, (spot: Spotting) => {
                return spot.endIndex >= headIndex && spot.startIndex <= headEndIndex;
            });

            if (headIndex !== -1 && !interception) {
                headSpot = {
                    startIndex: headIndex,
                    endIndex: headEndIndex,
                    type: detectionTypes.whitelistHead
                };
                return true;
            }

            return false;
        });

        if (headSpot) {
            if (item.complements && item.complements.length > 0) {
                let complementSpot: Spotting | undefined;
                item.complements.find(({ text }) => {
                    const complementIndex = lowerTranscript.indexOf(text.toLowerCase(), headSpot?.endIndex || 0);
                    const complementEndIndex = complementIndex + text.length - 1;

                    const interception = find(currentList, (spot: Spotting) => {
                        return spot.endIndex >= complementIndex && spot.startIndex <= complementEndIndex;
                    });

                    if (complementIndex !== -1 && !interception) {
                        complementSpot = {
                            startIndex: complementIndex,
                            endIndex: complementEndIndex,
                            type: detectionTypes.whitelistComplement
                        };
                        return true;
                    }

                    return false;
                });

                if (complementSpot) {
                    // if CKW
                    result.push(headSpot);
                    result.push(complementSpot);
                }
            } else {
                // if not CKW
                result.push(headSpot);
            }
        }
    });

    return result;
};

const getSpottingIndicesForBlacklist = (transcript: string, list: BlacklistPhraseItem[], currentList: Spotting[]) => {
    const result: Spotting[] = [];
    const lowerTranscript = transcript.toLowerCase();

    list.forEach(item => {
        const index = lowerTranscript.indexOf(item.text.toLowerCase());
        const interception = find(currentList, (spot: Spotting) => {
            return spot.endIndex >= index && spot.startIndex <= index + item.text.length - 1;
        });
        if (index !== -1 && !interception) {
            result.push({
                startIndex: index,
                endIndex: index + item.text.length - 1,
                type: 'blacklist'
            });
        }
    });
    return result;
};

const getSpottingType = (spottingIndices: Spotting[], startIndex: number, endIndex: number) => {
    const spotting = find(spottingIndices, (item: Spotting) => {
        return item.startIndex <= startIndex && item.endIndex >= endIndex;
    });

    return spotting ? spotting.type : detectionTypes.normal;
};

const combineNums = (words: TranscriptWordItem[]) => {
    const result: TranscriptWordItem[] = [];
    let previousWord: TranscriptWordItem | undefined;
    words.forEach((word: TranscriptWordItem) => {
        if (previousWord && previousWord.normalizedWord === '<NUM>' && word.normalizedWord !== previousWord.normalizedWord) {
            result.push(previousWord);
        }
        previousWord = word;
        if (word.normalizedWord === '<NUM>') return;

        result.push(word);
    });

    if (previousWord && previousWord.normalizedWord === '<NUM>') {
        result.push(previousWord);
    }

    return result;
};

export const parseTranscript = (transcript: TranscriptItem, whitelist: PhraseItem[], blacklist: BlacklistPhraseItem[]) => {
    const { normalizedTranscript, words, type: source } = transcript;
    const render: TranscriptRenderWord[] = [];

    const whitelistSpotting = getSpottingIndicesForWhitelist(normalizedTranscript, whitelist);
    const blacklistSpotting = getSpottingIndicesForBlacklist(normalizedTranscript, blacklist, whitelistSpotting);
    const spottingIndices = whitelistSpotting.concat(blacklistSpotting);

    let index = 0;
    combineNums(words).forEach(word => {
        render.push({
            text: word.normalizedWord,
            source,
            spotting: getSpottingType(spottingIndices, index, index + word.normalizedWord.length - 1),
            anonymized: Boolean(word.words[0].anonymized),
            confidence: word.confidence,
            pronunciation: word.pronunciation,
            wordOffsetStart: word.words[0].startTimeMsec,
            wordOffsetEnd: word.words[0].endTimeMsec
        });

        index += word.normalizedWord.length;
        const punctuationText =
            word.punctuation.name && word.punctuation.name !== ' ' ? `${word.punctuation.name} ` : word.punctuation.name;

        render.push({
            text: punctuationText,
            source,
            spotting: getSpottingType(spottingIndices, index, index + punctuationText.length),
            anonymized: false
        });

        index += punctuationText.length;
    });

    return [
        {
            render,
            text: transcript.normalizedTranscript
        }
    ];
};

export const combineTimelineTranscript = (transcripts: TranscriptItem[], whitelist: PhraseItem[], blacklist: BlacklistPhraseItem[]) => {
    if (!transcripts.length) return [];

    const NEW_PHRASE_THRESHOLD = 2000;
    const orderedTranscripts = orderBy(transcripts, 'startOffset');
    const resultTranscript: CombinedTranscriptItem[] = [];
    let lastTranscript: Partial<TranscriptItem> = {};
    let currentBatch: CombinedTranscriptItem | null = null;

    for (const transcript of orderedTranscripts) {
        let parsedTranscript: CombinedTranscriptItem | null = null;
        try {
            parsedTranscript = {
                ...transcript,
                transcript: parseTranscript(transcript, whitelist, blacklist)
            };
        } catch {
            const message =
                'Cannot parse transcript, because it has wrong format. It could happen because it was persist in local storage with one version of application and rehydrate with another.';

            console.warn(message);
            console.warn(`Transcript: ${safeStringify(orderedTranscripts)}`);
            captureWarnMessage(message);
            return [];
        }

        if (!parsedTranscript) {
            continue;
        }

        if (
            parsedTranscript.type !== lastTranscript.type ||
            parsedTranscript.startOffset > ((lastTranscript.endOffset as number | undefined) || 0) + NEW_PHRASE_THRESHOLD
        ) {
            if (lastTranscript.type && currentBatch) resultTranscript.push(currentBatch);
            currentBatch = { ...parsedTranscript };
        } else {
            const currentTranscript = currentBatch?.transcript || [];
            currentBatch = {
                ...currentBatch,
                transcript: [...currentTranscript, ...parsedTranscript.transcript],
                endOffset: parsedTranscript.endOffset
            } as CombinedTranscriptItem;
        }
        lastTranscript = parsedTranscript;
    }

    if (currentBatch) {
        resultTranscript.push(currentBatch);
    }

    return resultTranscript;
};

export const getTranscriptString = (
    timeTranscript: { transcript: { text: string }[]; type: string }[],
    localization: { getText: (key: string) => string }
) => {
    const result = timeTranscript.map(transcriptBlock => {
        const transcript = transcriptBlock.transcript.map((item: { text: string }) => item.text).join(' ');

        const name = localization.getText(`trainer.transcript.name.${transcriptBlock.type}`);
        return `${name}: ${transcript}`;
    });

    return result.join('\n\n');
};
